import { ApiMetric } from "@incendium/api";
import {
  formatRate,
  formatRank,
  formatCurrency,
  formatSeconds,
  formatNumberValue,
  formatIntegerValue,
} from "./formatters";
import {
  getExcludedDimensions,
  getCompatibleDimensions,
} from "./dimensionCompatibility";
import {
  getAttributionMapping,
  attributionMetricsThatUseSpend,
} from "./attributionMapping";
import { AttributionMetric } from "features/analytics/types/types";
import { formatRatio } from "./formatters/rateFormatter";

enum BaseMetricType {
  CURRENCY = "currency",
  RATE = "rate",
  NUMBER = "number",
  INTEGER = "integer",
  RATIO = "ratio",
}

// Define base metric types based on attribution metric
const baseMetricTypeMap: Record<AttributionMetric, BaseMetricType> = {
  [AttributionMetric.REVENUE]: BaseMetricType.CURRENCY,
  [AttributionMetric.SALES]: BaseMetricType.NUMBER,
  [AttributionMetric.MACRO]: BaseMetricType.NUMBER,
  [AttributionMetric.MACRO_RATE]: BaseMetricType.RATE,
  [AttributionMetric.PROFIT]: BaseMetricType.CURRENCY,
  [AttributionMetric.NET_REVENUE]: BaseMetricType.CURRENCY,
  [AttributionMetric.ROI]: BaseMetricType.RATE,
  [AttributionMetric.LTV]: BaseMetricType.CURRENCY,
  [AttributionMetric.ROILTV]: BaseMetricType.CURRENCY, // This should be currency, not rate
  [AttributionMetric.ROAS]: BaseMetricType.RATIO,
  [AttributionMetric.ROASLTV]: BaseMetricType.CURRENCY, // This should be currency, not rate
  [AttributionMetric.CUMULATIVE_LTV]: BaseMetricType.CURRENCY,
  [AttributionMetric.CUMULATIVE_LTV_PROFIT]: BaseMetricType.CURRENCY,
  [AttributionMetric.NEW_CUSTOMER]: BaseMetricType.INTEGER,
  [AttributionMetric.CAC]: BaseMetricType.CURRENCY,
  [AttributionMetric.AOV]: BaseMetricType.CURRENCY,
};

export const getMetricFormatter = (metric: ApiMetric) => {
  // First check if it's an attribution metric
  const attributionMapping = getAttributionMapping(metric);
  if (attributionMapping.attribtionMetric) {
    const baseType = baseMetricTypeMap[attributionMapping.attribtionMetric];
    switch (baseType) {
      case BaseMetricType.CURRENCY:
        return formatCurrency;
      case BaseMetricType.RATE:
        return formatRate;
      case BaseMetricType.NUMBER:
        return formatNumberValue;
      case BaseMetricType.INTEGER:
        return formatIntegerValue;
      case BaseMetricType.RATIO:
        return formatRatio;
    }
  }

  // Non-attribution metrics - handle specific cases
  const rateMetrics = [
    ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
    ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
    ApiMetric.METRIC_RETURNING_LEAD_PERCENTAGE,
    ApiMetric.METRIC_EFFECTIVE_SESSIONS_TO_CONVERSION_RATE,
    ApiMetric.METRIC_MACRO_CONVERSION_RATE,
    ApiMetric.METRIC_PAGE_MICRO_CONVERSION_RATE,
    ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_TO_CONVERSION_RATE,
    ApiMetric.METRIC_PAGE_MACRO_CONVERSION_RATE,
    ApiMetric.METRIC_PAGE_MICRO_CONVERSION_RATE,
    ApiMetric.METRIC_LEAD_MACRO_CONVERSION_PERCENTAGE,
    ApiMetric.METRIC_AVERAGE_EFFECTIVE_PAGEVIEWS_RATE_DURING_SESSION,
    ApiMetric.METRIC_AVERAGE_VIEWED_PERCENTAGE,
    ApiMetric.METRIC_AVERAGE_SCROLL_PERCENTAGE,
    ApiMetric.METRIC_AVERAGE_VIEWED_PERCENTAGE_DURING_SESSION,
    ApiMetric.METRIC_AVERAGE_SCROLL_PERCENTAGE_DURING_SESSION,
    ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
    ApiMetric.METRIC_VISIT_TO_APPLICATION_RATE,
    ApiMetric.METRIC_RETURNING_NINETY_DAY_CUSTOMER_PERCENTAGE,
    ApiMetric.METRIC_SALES_COMPLETION_RATE,
    ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_RATE,
    ApiMetric.METRIC_PRODUCT_IMPRESSION_RATE,
    ApiMetric.METRIC_JOB_CLICK_THROUGH_RATE,
    ApiMetric.METRIC_JOB_IMPRESSION_RATE,
    ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
    ApiMetric.METRIC_PAGE_JOB_APPLICATIONS_RATE,
    ApiMetric.METRIC_PAGE_JOB_APPLY_CLICK_RATE,
    ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE,
    ApiMetric.METRIC_LANDING_PAGE_SESSION_MACRO_CONVERSION_RATE,
    ApiMetric.METRIC_BOUNCE_RATE,
    ApiMetric.METRIC_EXIT_RATE,
    ApiMetric.METRIC_PUBLISHER_CTR,
    ApiMetric.METRIC_TO_NEW_CUSTOMER_RATE,
    ApiMetric.METRIC_RETURNING_CUSTOMER_RATE,
    ApiMetric.METRIC_RETURNING_CUSTOMER_SESSION_MACRO_CONVERSION_RATE,
    ApiMetric.METRIC_CHECKOUT_START_RATE,
    ApiMetric.METRIC_CHECKOUT_COMPLETION_RATE,
    ApiMetric.METRIC_PRODUCT_PAGE_EFFECTIVENESS_RATE,
    ApiMetric.METRIC_CART_ABANDONMENT_RATE,
    ApiMetric.METRIC_CHECKOUT_ABANDONMENT_RATE,
    ApiMetric.METRIC_REPEAT_CUSTOMER_RATE,
    ApiMetric.METRIC_REPEAT_PURCHASE_RATE,
    ApiMetric.METRIC_CALCULATED_CTR,
    ApiMetric.METRIC_PUBLISHER_CLICKS_DIFFERENCE,
    ApiMetric.METRIC_VELOCITY_SESSION_MACRO_CONVERSION_RATE,
    ApiMetric.METRIC_NOT_VELOCITY_SESSION_MACRO_CONVERSION_RATE,
    ApiMetric.METRIC_VELOCITY_SESSION_MACRO_CONVERSION_RATE_UPLIFT,
    ApiMetric.METRIC_EXISTING_CUSTOMER_REVENUE_GROWTH_RATE,
    ApiMetric.METRIC_AVERAGE_MARGIN_PERCENT,
    ApiMetric.METRIC_SESSION_PRODUCT_PAGE_VIEWED_RATE,
  ];

  if (rateMetrics.includes(metric)) {
    return formatRate;
  }

  const ratioMetrics = [ApiMetric.METRIC_PUBLISHER_ROAS];

  if (ratioMetrics.includes(metric)) {
    return formatRatio;
  }

  // Currency metrics (non-attribution)
  const currencyMetrics = [
    ApiMetric.METRIC_SESSION_NET_REVENUE,
    ApiMetric.METRIC_SALES_REVENUE,
    ApiMetric.METRIC_SESSION_PROFIT,
    ApiMetric.METRIC_AVERAGE_SALE_VALUE,
    ApiMetric.METRIC_PRODUCT_REVENUE,
    ApiMetric.METRIC_PRODUCT_PROFIT,
    ApiMetric.METRIC_PRODUCT_GATEWAY_LTV,
    ApiMetric.METRIC_SPEND,
    ApiMetric.METRIC_CPA,
    ApiMetric.METRIC_CAC,
    ApiMetric.METRIC_CPNV,
    ApiMetric.METRIC_SESSION_CONTRIBUTION_MARGIN,
    ApiMetric.METRIC_REPEAT_CUSTOMERS_REVENUE,
    ApiMetric.METRIC_VELOCITY_SESSION_REVENUE,
    ApiMetric.METRIC_VELOCITY_SESSION_REVENUE_IMPACT,
    ApiMetric.METRIC_PUBLISHER_CPC,
    ApiMetric.METRIC_LEAD_LIFETIME_REVENUE,
  ];

  if (currencyMetrics.includes(metric)) {
    return formatCurrency;
  }

  // Time metrics
  const timeMetrics = [
    ApiMetric.METRIC_AVERAGE_SESSION_DURATION,
    ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
    ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE_DURING_SESSION,
  ];

  if (timeMetrics.includes(metric)) {
    return formatSeconds;
  }

  // Rank metric (special case)
  if (metric === ApiMetric.METRIC_RANK) {
    return formatRank;
  }

  // Integer metrics
  const integerMetrics = [
    ApiMetric.METRIC_CUSTOMERS_COUNT,
    ApiMetric.METRIC_SALE_ITEMS_QUANTITY,
    ApiMetric.METRIC_NEW_CUSTOMERS_COUNT,
    ApiMetric.METRIC_RETURNING_CUSTOMERS_COUNT,
    ApiMetric.METRIC_REPEAT_CUSTOMERS_COUNT,
    ApiMetric.METRIC_LEADS_COUNT,
    ApiMetric.METRIC_SESSIONS_COUNT,
    ApiMetric.METRIC_PAGEVIEWS_COUNT,
    ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_COUNT,
    ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT,
    ApiMetric.METRIC_JOB_CLICK_THROUGH_COUNT,
    ApiMetric.METRIC_JOB_IMPRESSIONS_COUNT,
  ];
  if (integerMetrics.includes(metric)) {
    return formatIntegerValue;
  }

  const daysMetrics = [ApiMetric.METRIC_AVERAGE_DAYS_TO_SECOND_PURCHASE];

  if (daysMetrics.includes(metric)) {
    return (value: number, b, c) => {
      const days = Number(value);
      if (!Number.isFinite(days) || days <= 0) {
        return "-";
      }
      return `${days} days`;
    };
  }

  // Default to number formatting
  return formatNumberValue;
};

export const getMetricConfig = (metric: ApiMetric) => {
  const formatter = getMetricFormatter(metric);
  const attributionMapping = getAttributionMapping(metric);
  const excludedDimensions = getExcludedDimensions(metric);
  const compatibleDimensions = getCompatibleDimensions(metric);

  return {
    format: formatter,
    ...attributionMapping,
    ...(excludedDimensions.length > 0 && { excludedDimensions }),
    ...(compatibleDimensions.length > 0 && { compatibleDimensions }),
  };
};

// Export the compatibility function
export { metricIsCompatible } from "./dimensionCompatibility";

// Export attribution functions
export {
  metricByAttribution,
  metricsByAttributionModelType,
  metricsByAttribution,
  allAttributionMetrics,
} from "./attributionMapping";

// Legacy functions for backward compatibility
export const metricConfigByName = (metric: ApiMetric) => {
  // Use the new modular system
  return getMetricConfig(metric);
};

// Currency metrics list - keeping this as it's used elsewhere
export const centsToDollarMetrics = [
  ApiMetric.METRIC_REVENUE,
  ApiMetric.METRIC_SALES_REVENUE,
  ApiMetric.METRIC_SESSION_PROFIT,
  ApiMetric.METRIC_AVERAGE_SALE_VALUE,
  ApiMetric.METRIC_PRODUCT_REVENUE,
  ApiMetric.METRIC_PRODUCT_PROFIT,
  ApiMetric.METRIC_PRODUCT_GATEWAY_LTV,
  ApiMetric.METRIC_LEAD_LIFETIME_REVENUE,
  ApiMetric.METRIC_PUBLISHER_CPC,
  ApiMetric.METRIC_CPA,
  ApiMetric.METRIC_CAC,
  ApiMetric.METRIC_CPNV,
  ApiMetric.METRIC_SESSION_CONTRIBUTION_MARGIN,
  ApiMetric.METRIC_REPEAT_CUSTOMERS_REVENUE,
  ApiMetric.METRIC_VELOCITY_SESSION_REVENUE,
  ApiMetric.METRIC_VELOCITY_SESSION_REVENUE_IMPACT,
  ApiMetric.METRIC_REVENUE_FIRST,
  ApiMetric.METRIC_REVENUE_LAST,
  ApiMetric.METRIC_REVENUE_LAST_NON_DIRECT,
  ApiMetric.METRIC_REVENUE_LINEAR,
  ApiMetric.METRIC_REVENUE_IMPACTED,
  ApiMetric.METRIC_REVENUE_EBA,
  ApiMetric.METRIC_REVENUE_POSITION_BASED,
  ApiMetric.METRIC_LTV_FIRST,
  ApiMetric.METRIC_LTV_LAST,
  ApiMetric.METRIC_LTV_LAST_NON_DIRECT,
  ApiMetric.METRIC_LTV_LINEAR,
  ApiMetric.METRIC_LTV_IMPACTED,
  ApiMetric.METRIC_LTV_EBA,
  ApiMetric.METRIC_REVENUE_CUSTOM,
];

const nonAttributionMetricsThatUseSpend = [
  ApiMetric.METRIC_SPEND,
  ApiMetric.METRIC_CPA,
  ApiMetric.METRIC_CPNV,
  ApiMetric.METRIC_SESSION_CONTRIBUTION_MARGIN,
  ApiMetric.METRIC_SESSION_EFFICIENCY_RATIO,
  ApiMetric.METRIC_LTV_CAC_RATIO,
  ApiMetric.METRIC_CAC,
];

// Publisher data metrics (clicks, conversions, impressions, etc.)
const publisherDataMetrics = [
  ApiMetric.METRIC_PUBLISHER_CLICKS,
  ApiMetric.METRIC_PUBLISHER_CONVERSIONS,
  ApiMetric.METRIC_PUBLISHER_ALL_CONVERSIONS,
  ApiMetric.METRIC_PUBLISHER_SALES_COUNT,
  ApiMetric.METRIC_PUBLISHER_REVENUE,
  ApiMetric.METRIC_PUBLISHER_IMPRESSIONS,
  ApiMetric.METRIC_PUBLISHER_CTR,
  ApiMetric.METRIC_PUBLISHER_CPC,
];

export const usesPublisherDataMetrics: ApiMetric[] = [
  ...nonAttributionMetricsThatUseSpend,
  ...publisherDataMetrics,
  ...attributionMetricsThatUseSpend(),
];
